/**
 * Filtros da tela de Pagamentos (AF-4139).
 *
 * O formulário é um rascunho: nada vale até "Aplicar", que também fecha o modal. "Reset" volta o
 * rascunho ao padrão sem aplicar. As opções de tipo, meio e tipo de bem são as mesmas do Registrar
 * Pagamento, e a conta de recebimento vem do mesmo select de contas ativas.
 */
import { useState } from 'react';

import Dialog from '../../../organisms/Dialog';
import CurrencyInput from '../../../molecules/CurrencyInput';
import SelectTextField from '../../../molecules/SelectTextField';
import TextEdit from '../../../molecules/TextEdit';
import ReceivingBankAccountSelect from '../../../molecules/payment/ReceivingBankAccountSelect';
import { STATUS_META } from '../PaymentsPanel';
import { PaymentStatus } from '../services/billingApi';
import {
    AmountOperator, DEFAULT_PAYMENT_FILTERS, PaymentFilters,
} from '../services/paymentsFilter';
import {
    BILLING_PAYMENT_METHODS, PaymentType, assetTypeLabels, paymentMethodLabels, paymentTypeLabels,
} from '../../../../constants/payment';

const STATUSES: PaymentStatus[] = ['PENDING', 'MARKED', 'IDENTIFIED'];

const OPERATORS: { value: AmountOperator; label: string }[] = [
    { value: 'EQ', label: 'É' },
    { value: 'LT', label: 'Menor que' },
    { value: 'GT', label: 'Maior que' },
    { value: 'BETWEEN', label: 'Entre valores' },
];

const options = (labels: Record<string, string>, keys = Object.keys(labels)) =>
    keys.map(key => ({ value: key, label: labels[key] }));

interface Props {
    value: PaymentFilters;
    onApply: (filters: PaymentFilters) => void;
    onClose: () => void;
}

const PaymentsFilterModal: React.FC<Props> = ({ value, onApply, onClose }) => {
    const [draft, setDraft] = useState<PaymentFilters>(value);
    const set = (patch: Partial<PaymentFilters>) => setDraft(current => ({ ...current, ...patch }));

    const toggleStatus = (status: PaymentStatus) => set({
        statuses: draft.statuses.includes(status)
            ? draft.statuses.filter(s => s !== status)
            : STATUSES.filter(s => s === status || draft.statuses.includes(s)),
    });

    const body = (
        <div className="px-6 py-4 space-y-4 w-[34rem] max-w-full">
            <div className="flex gap-2">
                {STATUSES.map(status => {
                    const on = draft.statuses.includes(status);
                    return (
                        <button key={status} type="button" aria-pressed={on} onClick={() => toggleStatus(status)}
                            className={`flex-1 text-sm px-3 py-1.5 rounded border inline-flex items-center justify-center gap-2 ${on
                                ? 'border-gray-400 bg-gray-100 dark:bg-slate-700 font-medium'
                                : 'border-gray-200 text-gray-400'}`}>
                            <span className="w-2 h-2 rounded-full shrink-0"
                                style={{ backgroundColor: on ? STATUS_META[status].hex : 'transparent',
                                         border: on ? undefined : '1px solid currentColor' }} />
                            {STATUS_META[status].label}
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-3 gap-3 items-end">
                <SelectTextField id="payments-filter-amount-operator" label="Valor Extrato" className="mb-0"
                    value={draft.amountOperator} emptyLabel="Qualquer" items={OPERATORS}
                    onChange={e => set({ amountOperator: e.target.value })} />
                {draft.amountOperator && (
                    <CurrencyInput id="payments-filter-amount" label={draft.amountOperator === 'BETWEEN' ? 'De' : 'Valor'}
                        className="mb-0" fractionDigits={2} value={draft.amount ?? 0}
                        onChange={amount => set({ amount })} />
                )}
                {draft.amountOperator === 'BETWEEN' && (
                    <CurrencyInput id="payments-filter-amount-to" label="Até" className="mb-0"
                        fractionDigits={2} value={draft.amountTo ?? 0}
                        onChange={amountTo => set({ amountTo })} />
                )}
            </div>

            <div className="grid grid-cols-2 gap-3">
                <SelectTextField id="payments-filter-type" label="Tipo de Pagamento" className="mb-0"
                    value={draft.paymentType} emptyLabel="Todos" items={options(paymentTypeLabels)}
                    onChange={e => set({ paymentType: e.target.value, paymentMethod: '', assetType: '' })} />
                {draft.paymentType === PaymentType.FINANCIAL && (
                    <SelectTextField id="payments-filter-method" label="Método de Pagamento" className="mb-0"
                        value={draft.paymentMethod} emptyLabel="Todos"
                        items={options(paymentMethodLabels, BILLING_PAYMENT_METHODS)}
                        onChange={e => set({ paymentMethod: e.target.value })} />
                )}
                {draft.paymentType === PaymentType.ASSET_DATION && (
                    <SelectTextField id="payments-filter-asset" label="Tipo de Bem" className="mb-0"
                        value={draft.assetType} emptyLabel="Todos" items={options(assetTypeLabels)}
                        onChange={e => set({ assetType: e.target.value })} />
                )}
            </div>

            <div className="grid grid-cols-2 gap-3">
                <TextEdit id="payments-filter-payer-name" label="Nome Pagador" value={draft.payerName}
                    onChange={e => set({ payerName: e.target.value })} />
                <TextEdit id="payments-filter-payer-document" label="Documento Pagador" value={draft.payerDocument}
                    onChange={e => set({ payerDocument: e.target.value })} />
            </div>

            <ReceivingBankAccountSelect id="payments-filter-account" value={draft.accountAlias}
                onChange={accountAlias => set({ accountAlias })} />
        </div>
    );

    const footer = (
        <div className="flex gap-2">
            <button type="button" onClick={() => setDraft(DEFAULT_PAYMENT_FILTERS)}
                className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                Reset
            </button>
            <button type="button" onClick={() => { onApply(draft); onClose(); }}
                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">
                Aplicar
            </button>
        </div>
    );

    return <Dialog id="payments-filter" show onClose={onClose} header="Filtros" body={body} footer={footer} size="lg" />;
};

export default PaymentsFilterModal;
